<script lang="ts">
	// Design system components
	import {
		Alert,
		Button,
		Collapse,
		Container,
		IconButton,
		Loader,
		Select,
		Stack,
		Toggle,
		Text,
		Popover,
	} from '@Shelly/shelly-design-system';
	// Services
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { format } from 'svelte-i18n';
	// Helpers
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import {
		handleHysteresisError,
		loadScheduleProfiles,
		prepareScheduleToSend,
		translateThermostatData,
	} from './helpers';
	import { getDailyRangeOptions, startsWeekOnSun } from 'src/helpers/date_helper';
	import { parseSchedules } from 'src/helpers/actions/schedules/core/schedule_helpers';
	// Components
	import ThermostatControl from './Components/ThermostatControl.svelte';
	import EditThermostatG2Wizard from './EditThermostatG2Wizard.svelte';
	import LayerChartRenderer from 'src/components/Chart/LayerChart/LayerChartRenderer.svelte';
	import SchedulesList from 'src/components/DevicesSettings/Schedules/SchedulesHelpers/SchedulesList.svelte';
	import EditSchedulesG2Wizard from './EditSchedulesG2Wizard.svelte';
	import ModalPrompt from 'src/components/ModalPrompt.svelte';
	import EmptyList from 'src/components/Lists/EmptyList.svelte';
	import TransitionWrapper from 'src/components/Utility/TransitionWrapper.svelte';
	import AddSchedulesG2Wizard from './AddSchedulesG2Wizard.svelte';
	import ScheduleProfilesEditor from './Components/ScheduleProfilesEditor.svelte';
	// Types
	import type { IChartTempSchedule } from 'src/types/TemperatureSchemeType';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { ScheduleRequestData } from 'src/types/requests/schedules/schedules_g2';
	// Svelte hooks
	import { beforeUpdate, onMount } from 'svelte';
	// Stores
	import {
		getDeviceSettings,
		getDeviceStatus,
		readDeviceStatus,
	} from 'src/stores/device-runtime/status-selectors';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	// Configs
	import { getTempSchemeChartConfig } from 'src/helpers/statistics/config/temp_scheme';
	import DecreaseIncreaseInput from 'src/components/DecreaseIncreaseInput.svelte';
	import { real_id } from 'src/helpers/interface_helpers';
	import { navigateToSetting } from 'src/stores/ui/navigate-to';
	import { getWallDisplayTemperatureSensorError } from './Components/wall_display_thermostat_alerts';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';

	$: settingsStore = getDeviceSettings(device.id);
	export let thermostat, device;
	/** `control`: wheel only (main WD thermostat-on). `extractedMain`: centered wheel + settings below. */
	export let variant: 'full' | 'control' | 'extractedMain' = 'full';

	const loading = { enable: {}, mode: {} };
	const promptProps = {
		title: capFirstChar($format('button_delete')),
		callback: undefined,
		button_1: $format('button_delete'),
		button_2: $format('button_cancel'),
		text: undefined,
		subtext: undefined,
	};

	$: statusStore = getDeviceStatus(device.id);
	$: status = $statusStore;
	$: settings = $settingsStore;

	$: currentTemperature = status?.['thermostat:0']?.current_C;
	$: enabledSchedules = status?.['thermostat:0']?.schedules?.enable;
	$: thermostatEnabled = status?.['thermostat:0']?.enable;

	let schedules;
	let translateData: IChartTempSchedule;
	let chartData;
	let startWeekOnSun = startsWeekOnSun();
	let unit = settings?.['thermostat:0']?.display_unit;
	let rangeOptions = getDailyRangeOptions(startWeekOnSun);
	let rangeValue = rangeOptions[0];
	let showModal: boolean = false;
	let showScheduleEditor = false;
	let showAddScheduleEditor = false;
	let showDeleteDialog = false;
	let checkProfiles = false;
	let currentProfile;
	let enableThermostatLoading = false;
	let loadingSchedules = false;
	let loadingHysteresis = false;
	let expandThermostatSettings = false;
	let showHysteresisPopover = false;
	let minValueHysteresis = 0;
	let maxValueHysteresis = 2;
	let hysteresisError = '';

	let editableObject = {
		activeIndex: 0,
		activeProfile: readDeviceStatus(device.id)?.['thermostat:0']?.schedules?.profile_id,
		callback: undefined,
		category: undefined,
		condition: undefined,
		loading: false,
		device,
		thermostat,
		disabled: false,
		mode: undefined,
		options: undefined,
		selectedDevices: undefined,
		selectedAction: null,
		profileName: readDeviceStatus(device.id)?.['thermostat:0']?.schedules?.profile_name,
		profiles: { 0: { rules: [] } },
		schedule: undefined,
		_options: [],
	}; //used on close modal to restore editableObject to its def values

	$: activeRules = editableObject.profiles?.[editableObject.activeProfile]?.rules;

	const loadData = async () => {
		if (
			typeof editableObject.activeProfile == 'undefined' &&
			editableObject.activeProfile !== currentProfile
		) {
			editableObject.activeProfile = 0;
		}

		editableObject.profiles = await loadScheduleProfiles(device, editableObject.activeProfile, unit);

		if (!Object.keys(editableObject.profiles).includes(`${editableObject.activeProfile}`)) {
			editableObject.activeProfile = 0;
		}
		currentProfile = editableObject.activeProfile;
		schedules = {
			currentProfile,
			profileName: editableObject.profileName,
			currentTemperature,
		};
		if (typeof editableObject.profileName === 'undefined') {
			editableObject.profileName =
				editableObject.profiles[editableObject.activeProfile]?.name || 'unnamed';
		}
		translateData = translateThermostatData(
			{
				...thermostat,
				schedules: editableObject.profiles[editableObject.activeProfile]?.rules || [],
			},
			editableObject.thermostat['target_C'],
			unit,
		);
		chartData = getTempSchemeChartConfig(rangeValue.value, translateData, startWeekOnSun, true);
	};

	const editThermostat = (id) => {
		//open modal
		showModal = true;
	};

	const onChangeRange = (range: number) => {
		rangeValue = rangeOptions.find((el) => el.value === range);
		chartData = getTempSchemeChartConfig(range, translateData, startWeekOnSun, true);
	};

	const selectChangeRange = (e: CustomEvent | { detail: number }) => {
		onChangeRange(e.detail.value);
	};

	const toggleSchedules = async (e) => {
		loadingSchedules = true;
		const enable = e.target.checked;
		const res: SettingRequest = await gen2Request(device, 'Thermostat.Schedule.SetConfig', {
			config: {
				id: 0,
				enable,
			},
		});

		if (res.isok) {
			enabledSchedules = enable;
			if (enable) loadData();
		}
		onSettingsResponse(res, device);
		loadingSchedules = false;
	};

	onMount(async () => {
		if (enabledSchedules && typeof editableObject.activeProfile !== 'undefined' && !checkProfiles) {
			await loadData(); //loading schedule lists if enabling schedules
			checkProfiles = true;
		}
	});

	beforeUpdate(() => {
		if (typeof currentProfile !== 'undefined' && editableObject.activeProfile !== currentProfile) {
			//caching profile to reload schedules list
			currentProfile = editableObject.activeProfile;
			loadData();
			if (typeof editableObject.profiles?.[editableObject?.activeProfile]?.rules === 'undefined') {
				editableObject.profiles[editableObject.activeProfile].rules = [];
			}
		}
		if (
			editableObject?.thermostat?.target_C !== thermostat?.target_C ||
			editableObject?.thermostat?.enable !== thermostat?.enable ||
			editableObject?.thermostat?.current_C !== thermostat?.current_C
		) {
			//reassigning editableObject.thermostat to current thermostat because it is possible to get changes from status
			editableObject.thermostat = thermostat;
		}
	});

	const editSchedule = (e) => {
		const index = e.detail.index;
		const rule = e.detail.rule;
		editableObject = {
			...editableObject,
			schedule: { index, ...rule },
		};
		showScheduleEditor = true;
	};

	const addSchedule = () => {
		const deviceStatus = readDeviceStatus(device.id);
		const defaultSchedule: any = {
				target_C: deviceStatus?.['thermostat:0'].target_C,
				selection: undefined,
				sun: undefined,
				time: '00:00',
				enable: true,
				timespec: '0 0 0 * * *',
				unit,
				profile_id: deviceStatus?.['thermostat:0']?.schedules?.profile_id || 0,
			},
			schedule = parseSchedules(defaultSchedule as ScheduleRequestData);
		editableObject = { ...editableObject, schedule };
		showAddScheduleEditor = true;
	};

	const initThermostat = async () => {
		loading[0] = true;
		const res = await gen2Request(device, 'Thermostat.Create', {});
		onSettingsResponse(res, editableObject.device);
		loading[0] = false;
	};

	const deleteSchedule = async (e) => {
		const res: SettingRequest = await gen2Request(device, 'Thermostat.Schedule.DeleteRule', {
			id: 0,
			rule_id: editableObject.profiles[editableObject.activeProfile].rules[e.detail].rule_id,
		});
		onSettingsResponse(res, device);
		if (res.isok) loadData();
	};
	const toggleSchedule = async (e) => {
		const schedule = e?.detail?.schedule;
		schedule.enable = !schedule.enable;

		const config = prepareScheduleToSend(schedule);
		const res = await gen2Request(editableObject.device, 'Thermostat.Schedule.ChangeRule', {
			...config,
		});
		onSettingsResponse(res, device);
		if (res.isok && schedule.enable) loadData();
	};

	const updateData = () => {
		if (
			enabledSchedules &&
			typeof editableObject.profiles[editableObject.activeProfile].rules !== 'undefined' &&
			Array.isArray(editableObject.profiles[editableObject.activeProfile].rules) &&
			Object.values(editableObject.profiles[editableObject.activeProfile].rules).every(
				(rule) => !rule.enable,
			)
		) {
			//reloading data if all schedules are disabled to reload chart data
			thermostat = editableObject.thermostat;
			loadData();
		}
	};

	/**
	 * Toggle the enable property of the thermostat and make a request to update it.
	 *
	 * @description This function toggles the enable property of the thermostat and makes a request to update it.
	 * @returns {Promise<void>}
	 */
	const toggleEnableThermostat = async () => {
		const data = thermostat;
		enableThermostatLoading = true;
		thermostatEnabled = !thermostatEnabled;
		data.enable = !data.enable;
		const res: SettingRequest = await gen2Request(device, 'Thermostat.SetConfig', {
			id: data.id,
			config: { ...data },
		});

		onSettingsResponse(res, device, {
			success: $format('setting_scc_saved_toast'),
			danger: $format('setting_err_toast'),
		});
		enableThermostatLoading = false;

		setSidePanelState({ showSettingsModal: false });
	};

	const saveThermostatHysteresis = async () => {
		loadingHysteresis = true;
		const data = thermostat;
		const res: SettingRequest = await gen2Request(device, 'Thermostat.SetConfig', {
			id: data.id,
			config: { ...data },
		});

		onSettingsResponse(res, device, {
			success: $format('setting_scc_saved_toast'),
			danger: $format('setting_err_toast'),
		});
		loadingHysteresis = false;
	};

	$: sensorError = getWallDisplayTemperatureSensorError(status);
</script>

{#if typeof thermostat !== 'undefined'}
	{#if variant === 'control'}
		<ThermostatControl
			hideInlineAlerts
			isLoading={enableThermostatLoading}
			disabled={!editableObject?.thermostat?.enable || enableThermostatLoading}
			{device}
			bind:thermostat={editableObject.thermostat}
			on:change={updateData}
		/>
	{:else}
		<div
			class={variant === 'extractedMain'
				? 'd-flex flex-direction-column flex-full-height'
				: ''}
		>
			{#if variant === 'extractedMain'}
				<div class="d-flex flex-direction-column flex-1 align-items-center justify-content-center">
					<ThermostatControl
						hideInlineAlerts
						isLoading={enableThermostatLoading}
						disabled={!editableObject?.thermostat?.enable || enableThermostatLoading}
						{device}
						bind:thermostat={editableObject.thermostat}
						on:change={updateData}
					/>
				</div>
			{/if}
			<Container class="mb-m" type="borderSolid" padding="xs">
				<div class="d-flex justify-content-between">
					<Toggle
						checked={thermostatEnabled}
						size="m"
						value={thermostatEnabled}
						class="mb-s"
						on:click={toggleEnableThermostat}
						id="toggle-r099b2w"
						>{$format('label_enable_thermostat', {
							default: 'Enable thermostat',
						})}</Toggle
					>
					<Stack direction="column" gap="s">
						<IconButton
							icon="fas fa-pen"
							on:click={() => editThermostat(thermostat.id)}
							aria-label={$format('button_edit', {
								default: 'Edit',
							})}
						/>
					</Stack>
				</div>
				{#if variant !== 'extractedMain'}
					<ThermostatControl
						isLoading={enableThermostatLoading}
						disabled={!editableObject?.thermostat?.enable || enableThermostatLoading}
						{device}
						bind:thermostat={editableObject.thermostat}
						on:change={updateData}
					/>

					{#if sensorError.present}
						{#if sensorError.driverMissing}
							<Alert
								class="mt-s mb-s"
								type="danger"
								isHeadingBold
								heading={$format('alert_heading_wall_display_sensor_firmware', {
									default: 'Temperature sensor unavailable',
								})}
								message={$format('alert_message_wall_display_sensor_firmware', {
									default:
										'This firmware does not include the temperature sensor. Update the device firmware to restore temperature readings.',
								})}
								cta={device?.shared
									? undefined
									: {
											title: $format('title_update_firmware_update', {
												default: 'Update Firmware',
											}),
										}}
								on:cta_click={() =>
									navigateToSetting(real_id(device.id), 'settings', 'firmware_version')}
							/>
						{:else}
							<Alert
								class="mt-s mb-s"
								type="danger"
								heading={$format('alert_danger_sensor_missing_wall_display', {
									default: 'Temperature sensor error detected.',
								})}
							/>
						{/if}
					{/if}
				{/if}

		<Toggle
			class={`mt-s ${enabledSchedules && thermostatEnabled ? 'mb-s' : ''}`}
			size="m"
			checked={enabledSchedules}
			disabled={!editableObject.thermostat.enable || enableThermostatLoading}
			on:click={toggleSchedules}
			id="toggle-vmbmqg9">{$format('toggle_schedules')}</Toggle
		>
		{#if thermostatEnabled && (loadingSchedules || (typeof chartData == 'undefined' && enabledSchedules))}
			<div class="d-flex justify-content-center">
				<Loader size="l" />
			</div>
		{/if}
		{#if thermostatEnabled && !enableThermostatLoading}
			{#key enabledSchedules}
				<TransitionWrapper>
					{#if typeof chartData !== 'undefined' && enabledSchedules}
						{#key editableObject.profileName}
							<ScheduleProfilesEditor {editableObject} />
							<div class="d-flex">
								<Select
									class=" mb-s"
									value={rangeValue}
									size="m"
									icon="fas fa-clock-rotate-left"
									options={rangeOptions}
									on:change={selectChangeRange}
									id="select-550y27s"
									aria-label={$format('select_range', {
										default: 'Select range',
									})}
								/>
							</div>
							<LayerChartRenderer {chartData} />
							<div class=" mb-m"></div>
						{/key}
					{/if}

					{#if enabledSchedules && activeRules}
						{#key activeRules}
							<SchedulesList
								{device}
								tempRange={false}
								schedule_rules={editableObject.profiles[editableObject.activeProfile]
									.rules}
								disabled={!enabledSchedules}
								on:delete={deleteSchedule}
								on:edit={editSchedule}
								on:toggle={toggleSchedule}
							/>
						{/key}

						<Button
							size="m"
							variant={'primary'}
							class={classMap({
								'mb-s': true,
								'mt-s':
									enabledSchedules &&
									activeRules &&
									editableObject.profiles[editableObject.activeProfile].rules?.length,
							})}
							width="full"
							on:click={addSchedule}
							>{$format('button_add_schedule', {
								default: 'Add schedule',
							})}</Button
						>
					{/if}
				</TransitionWrapper>
			{/key}
		{/if}
	</Container>

	<!-- title="Thermostat Settings" -->
	<Collapse
		id="collapse-dirsd13"
		title={$format('sub_settings_text_thermostat_settings', {
			default: 'Thermostat Settings',
		})}
		expanded={expandThermostatSettings}
		on:click={() => {
			expandThermostatSettings = !expandThermostatSettings;
		}}
	>
		<DecreaseIncreaseInput
			bind:error={hysteresisError}
			on:output={(e) => {
				thermostat.hysteresis = e.detail;
				hysteresisError = handleHysteresisError(
					thermostat?.hysteresis,
					minValueHysteresis,
					maxValueHysteresis,
				);
			}}
			bind:value={thermostat.hysteresis}
			hysteresis
			allowedDigAfterPoint={2}
		>
			<Text slot="description" element="h2" class="mb-s">
				{$format('label_hysteresis', { default: 'Hysteresis' })}
				<Popover
					hideCloseBtn={true}
					class="mr-m"
					show={showHysteresisPopover}
					placement="right"
					type="hover"
					on:close={() => (showHysteresisPopover = false)}
				>
					<IconButton
						icon="fa-solid fa-circle-info"
						variant="help"
						class="text-primary"
						on:click={() => {
							showHysteresisPopover = !showHysteresisPopover;
						}}
						aria-label={$format('label_hysteresis_info', {
							default: 'Hysteresis info',
						})}
						size="s"
					/>
					<svelte:fragment slot="customContent">
						<Text styleType="body-small"
							>{$format('description_hysteresis', {
								default:
									'Temperature hysteresis in a thermostat refers to the difference in temperature at which the thermostat turns on and turns off. When the temperature rises above the set point, the thermostat turns on the heating or cooling system. However, the temperature must drop below a certain point before the thermostat turns off the system. This temperature difference is known as hysteresis and helps prevent rapid cycling of the heating or cooling system, which can improve energy efficiency and reduce wear and tear on the equipment.',
							})}</Text
						>
					</svelte:fragment>
				</Popover>
			</Text>
		</DecreaseIncreaseInput>
		<!-- Save thermostat hysteresis button -->
		<Button
			icon="fas fa-save"
			variant="primary"
			size="m"
			width="full"
			class="mt-m"
			disabled={loadingHysteresis || hysteresisError !== ''}
			on:click={saveThermostatHysteresis}
		>
			{$format('button_save_settings', {
				default: 'Save Settings',
			})}
		</Button>
	</Collapse>

	{#if showModal && typeof editableObject.thermostat !== 'undefined'}
		<EditThermostatG2Wizard
			bind:showModal
			{editableObject}
			on:save={(e) => {
				thermostat = e.detail.thermostat;
				loadData();
			}}
		/>
	{/if}
	{#if showAddScheduleEditor}
		<AddSchedulesG2Wizard
			bind:showModal={showAddScheduleEditor}
			{editableObject}
			on:save={loadData}
		/>
	{/if}
	{#if showScheduleEditor}
		<EditSchedulesG2Wizard bind:showModal={showScheduleEditor} {editableObject} on:save={loadData} />
	{/if}
	{#if showDeleteDialog}
		<ModalPrompt bind:showModal={showDeleteDialog} props={promptProps} />
	{/if}
		</div>
	{/if}
{:else}
	<div class="flex-full-height justify-content-center">
		<EmptyList
			type="drawer"
			withoutMargin
			title={$format('no_thermostats_title', {
				default: 'There are no thermostats yet',
			})}
			description={$format('no_thermostat_descr', {
				default: 'Try creating a Thermostat for easier and/or scheduled temperature control.',
			})}
			btnText={$format('button_add_thermostat', {
				default: 'Add thermostat',
			})}
			loading={loading[0]}
			onClick={initThermostat}
		/>
	</div>
{/if}
